import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    DialogActions,
    Button,
    Field,
    Input,
    Dropdown,
    Option,
    Spinner,
    Badge,
    makeStyles,
    tokens,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';
import { Info24Regular, DocumentText20Regular, List20Regular } from '@fluentui/react-icons';
import type { ListColumn, SourceRegistrationRequest } from '@shared/dto/searchIndexer';

const useStyles = makeStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        minWidth: '500px',
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    hint: {
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground3,
        marginTop: '2px',
    },
    infoCard: {
        display: 'flex',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderLeft: '3px solid #0ea5e9',
        borderRadius: '8px',
    },
    infoIcon: {
        color: '#0ea5e9',
        flexShrink: 0,
        marginTop: '2px',
    },
    infoText: {
        fontSize: tokens.fontSizeBase200,
        color: '#0c4a6e',
        lineHeight: '1.6',
        margin: 0,
    },
    sectionDivider: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        paddingTop: '4px',
    },
    lockedSource: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '4px',
        backgroundColor: tokens.colorNeutralBackground3,
    },
});

type SiteListEntry = {
    id: string;
    displayName: string;
    webUrl: string;
    sourceType: 'Page' | 'Announcement';
};

interface Props {
    open: boolean;
    onClose: () => void;
    onRegister: (config: SourceRegistrationRequest) => Promise<void>;
    getSiteLists: () => Promise<SiteListEntry[]>;
    getSourceColumns: (listId: string) => Promise<ListColumn[]>;
    /** Skip the source picker and auto-select the site's Site Pages library — used by the
     * initial setup wizard, where Site Pages is a mandatory source rather than an admin choice. */
    lockToSitePages?: boolean;
}

export function RegisterSourceModal({
    open,
    onClose,
    onRegister,
    getSiteLists,
    getSourceColumns,
    lockToSitePages = false,
}: Props) {
    const styles = useStyles();

    const [lists, setLists] = useState<SiteListEntry[]>([]);
    const [listsLoading, setListsLoading] = useState(false);
    const [columns, setColumns] = useState<ListColumn[]>([]);
    const [columnsLoading, setColumnsLoading] = useState(false);

    const [selectedListId, setSelectedListId] = useState('');
    const [selectedListName, setSelectedListName] = useState('');
    const [selectedSourceType, setSelectedSourceType] = useState<'Page' | 'Announcement'>('Announcement');
    const [tagField, setTagField] = useState('');
    const [filterField, setFilterField] = useState('');
    const [filterOperator, setFilterOperator] = useState<'Equals' | 'NotEquals' | 'OneOf'>('Equals');
    const [filterValue, setFilterValue] = useState('');
    const [urlTemplate, setUrlTemplate] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isPageSource = selectedSourceType === 'Page';

    // Selects a list and loads its columns for the Tag Field picker — shared by the manual
    // dropdown and the lockToSitePages auto-select path below.
    const selectList = async (listEntry: SiteListEntry) => {
        setSelectedListId(listEntry.id);
        setSelectedListName(listEntry.displayName);
        setSelectedSourceType(listEntry.sourceType);
        setTagField('');
        setFilterField('');
        setColumnsLoading(true);
        try {
            const cols = await getSourceColumns(listEntry.id);
            setColumns(cols);
        } catch (e: any) {
            setError(e.message ?? 'Failed to load columns');
        } finally {
            setColumnsLoading(false);
        }
    };

    // Load lists when modal opens
    useEffect(() => {
        if (!open) return;
        setListsLoading(true);
        setError(null);
        setSelectedListId('');
        setSelectedListName('');
        setSelectedSourceType('Announcement');
        setColumns([]);
        setTagField('');
        setFilterField('');
        setFilterValue('');
        setUrlTemplate('');
        getSiteLists()
            .then(async ls => {
                setLists(ls);
                if (lockToSitePages) {
                    const sitePages = ls.find(l => l.sourceType === 'Page');
                    if (sitePages) {
                        await selectList(sitePages);
                    } else {
                        setError('Site Pages library not found on this site. It is required for indexing.');
                    }
                }
            })
            .catch(e => setError(e?.message || 'Failed to load lists'))
            .finally(() => setListsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, getSiteLists, lockToSitePages]);

    const handleListChange = (listId: string, listName: string) => {
        if (!listId) {
            setSelectedListId('');
            setSelectedListName(listName);
            setSelectedSourceType('Announcement');
            setTagField('');
            setFilterField('');
            setColumns([]);
            return;
        }
        const listEntry = lists.find(l => l.id === listId);
        if (!listEntry) return;
        selectList(listEntry);
    };

    const handleSubmit = async () => {
        if (!selectedListId) return;
        setSubmitting(true);
        setError(null);
        try {
            await onRegister({
                listId: selectedListId,
                listDisplayName: selectedListName,
                sourceType: selectedSourceType,
                tagFieldName: tagField ? tagField : undefined,
                filterFieldName: (!isPageSource && filterField) ? filterField : undefined,
                filterOperator: (!isPageSource && filterField) ? filterOperator : undefined,
                filterValue: (!isPageSource && filterField) ? filterValue : undefined,
                detailPageUrlTemplate: urlTemplate || '',
            });
        } catch (e: any) {
            setError(e.message ?? 'Registration failed');
        } finally {
            setSubmitting(false);
        }
    };

    // Tag fields are Choice/MultiChoice columns, or Lookup columns (resolved to the target
    // list's display value via readItemTag()) — free text or other types can't be used as a
    // tag, so exclude them from this dropdown.
    const tagFieldColumns = columns.filter(
        c => c.columnType === 'choice' || c.columnType === 'multiChoice' || c.columnType === 'lookup',
    );
    const filterFieldColumns = columns.filter(c => c.columnType === 'text' || c.columnType === 'choice' || c.columnType === 'multiChoice');

    const dialogTitle = isPageSource ? 'Register Site Pages Source' : 'Register Announcement Source';

    return (
        <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
            <DialogSurface>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogBody>
                    <DialogContent>
                        <div className={styles.content}>
                            {error && (
                                <MessageBar intent="error">
                                    <MessageBarBody>{error}</MessageBarBody>
                                </MessageBar>
                            )}

                            {/* Source picker */}
                            <div className={styles.row}>
                                <Field label="Content Source" required>
                                    {listsLoading ? (
                                        <Spinner size="tiny" label="Loading lists…" />
                                    ) : lockToSitePages ? (
                                        <div className={styles.lockedSource}>
                                            <DocumentText20Regular style={{ color: '#0ea5e9', flexShrink: 0 }} />
                                            <span>{selectedListName || 'Site Pages'}</span>
                                            <Badge appearance="tint" color="informative" style={{ marginLeft: 'auto' }}>
                                                Required
                                            </Badge>
                                        </div>
                                    ) : (
                                        <Dropdown
                                            placeholder="Select a list or library"
                                            value={selectedListName}
                                            onOptionSelect={(_, d) => {
                                                handleListChange(
                                                    d.optionValue as string,
                                                    d.optionText ?? '',
                                                );
                                            }}
                                        >
                                            {lists.map(l => (
                                                <Option
                                                    key={l.id}
                                                    value={l.id}
                                                    text={l.displayName}
                                                >
                                                    {l.sourceType === 'Page'
                                                        ? <DocumentText20Regular style={{ color: '#0ea5e9', flexShrink: 0 }} />
                                                        : <List20Regular style={{ color: '#64748b', flexShrink: 0 }} />
                                                    }
                                                    {l.displayName}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    )}
                                </Field>
                            </div>

                            {/* Site Pages — info card + optional tag field */}
                            {selectedListId && isPageSource && (
                                <>
                                    <div className={styles.infoCard}>
                                        <Info24Regular className={styles.infoIcon} />
                                        <p className={styles.infoText}>
                                            <strong>All published Site Pages</strong> on this site will be indexed
                                            automatically. Pages that have never been published (no major version yet)
                                            are skipped. Each page's canvas content will be chunked and embedded.
                                        </p>
                                    </div>
                                    <div className={styles.row}>
                                        <Field label="Tag Field" required>
                                            {columnsLoading ? (
                                                <Spinner size="tiny" />
                                            ) : (
                                                <Dropdown
                                                    placeholder="Select a column"
                                                    value={tagField}
                                                    onOptionSelect={(_, d) =>
                                                        setTagField(d.optionValue as string)
                                                    }
                                                >
                                                    {tagFieldColumns.map(c => (
                                                        <Option key={c.name} value={c.name}>
                                                            {c.displayName}
                                                        </Option>
                                                    ))}
                                                </Dropdown>
                                            )}
                                            <p className={styles.hint}>
                                                Field whose value is stored as a tag on each embedding row.
                                                Required before indexing can run for Site Pages.
                                            </p>
                                        </Field>
                                    </div>
                                </>
                            )}

                            {/* Announcement/List config */}
                            {selectedListId && !isPageSource && (
                                <div className={styles.sectionDivider}>
                                    <div className={styles.row}>
                                        <Field label="Tag Field (optional)">
                                            {columnsLoading ? (
                                                <Spinner size="tiny" />
                                            ) : (
                                                <Dropdown
                                                    placeholder="None"
                                                    value={tagField}
                                                    onOptionSelect={(_, d) =>
                                                        setTagField(d.optionValue as string)
                                                    }
                                                >
                                                    <Option value="">None</Option>
                                                    {tagFieldColumns.map(c => (
                                                        <Option key={c.name} value={c.name}>
                                                            {c.displayName}
                                                        </Option>
                                                    ))}
                                                </Dropdown>
                                            )}
                                            <p className={styles.hint}>
                                                Field whose value is stored as a tag on each embedding row.
                                            </p>
                                        </Field>
                                    </div>

                                    <div className={styles.row} style={{ marginTop: '12px' }}>
                                        <Field label="Filter Field (optional)">
                                            <Dropdown
                                                placeholder="No filter"
                                                value={filterField}
                                                onOptionSelect={(_, d) =>
                                                    setFilterField(d.optionValue as string)
                                                }
                                            >
                                                <Option value="">No filter</Option>
                                                {filterFieldColumns.map(c => (
                                                    <Option key={c.name} value={c.name}>
                                                        {c.displayName}
                                                    </Option>
                                                ))}
                                            </Dropdown>
                                            <p className={styles.hint}>
                                                Only index items matching this field condition.
                                            </p>
                                        </Field>
                                    </div>

                                    {filterField && (
                                        <>
                                            <div className={styles.row} style={{ marginTop: '12px' }}>
                                                <Field label="Filter Operator">
                                                    <Dropdown
                                                        value={filterOperator}
                                                        onOptionSelect={(_, d) =>
                                                            setFilterOperator(d.optionValue as typeof filterOperator)
                                                        }
                                                    >
                                                        <Option value="Equals">Equals</Option>
                                                        <Option value="NotEquals">Not Equals</Option>
                                                        <Option value="OneOf">One Of (comma-separated)</Option>
                                                    </Dropdown>
                                                </Field>
                                            </div>
                                            <div className={styles.row} style={{ marginTop: '12px' }}>
                                                <Field label="Filter Value" required>
                                                    <Input
                                                        value={filterValue}
                                                        onChange={(_, d) => setFilterValue(d.value)}
                                                        placeholder={
                                                            filterOperator === 'OneOf'
                                                                ? 'e.g. Published,Active'
                                                                : 'e.g. Published'
                                                        }
                                                    />
                                                </Field>
                                            </div>
                                        </>
                                    )}

                                    <div className={styles.row} style={{ marginTop: '12px' }}>
                                        <Field label="Detail Page URL Template" required>
                                            <Input
                                                value={urlTemplate}
                                                onChange={(_, d) => setUrlTemplate(d.value)}
                                                placeholder="/sites/intranet/news/DispForm.aspx?ID={ItemId}"
                                            />
                                            <p className={styles.hint}>
                                                Use {'{ItemId}'} as a placeholder for the list item ID.
                                            </p>
                                        </Field>
                                    </div>
                                </div>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="subtle" onClick={onClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={
                                !selectedListId ||
                                submitting ||
                                (isPageSource && !tagField) ||
                                (!isPageSource && !urlTemplate) ||
                                (!isPageSource && !!filterField && !filterValue)
                            }
                            icon={submitting ? <Spinner size="tiny" /> : undefined}
                        >
                            {submitting ? 'Registering…' : 'Register Source'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
